import React, { FC, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
    title?: string;
    isTeam?: boolean; // False = Madison, True = Team Sprint
};

export const MultipleRidersTable: FC<ResultsTableProps> = ({ values, title, isTeam = false }) => {
    // 1. HOOKY MUSÍ BÝT VŽDY NA ÚPLNÉM ZAČÁTKU KOMPONENTY 🚀
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [headers, rows] = useMemo(() => {
        if (!values || values.length === 0) return [[], []];
        const [h, ...r] = values;
        return [h, r];
    }, [values]);

    const countryColIndex = useMemo(() => headers.findIndex((h) => h && h.toLowerCase() === 'country'), [headers]);
    const qColIndex = useMemo(() => headers.findIndex((h) => h && h.toLowerCase() === 'q'), [headers]);
    const firstNameIndex = useMemo(() => headers.findIndex((h) => h && h.toLowerCase().includes('first')), [headers]);

    // Sloupec B (index 1) je náš svatý slučovací parametr pro jeden konkrétní tým
    const groupColumnIndex = 1; 

    // 🎯 SESKUPOVÁNÍ JEZDCŮ DO TÝMŮ
    const teams = useMemo(() => {
        if (rows.length === 0) return [];
        const result: string[][][] = [];
        let current: string[][] = [];

        rows.forEach((row, idx) => {
            if (idx > 0 && row[groupColumnIndex] !== rows[idx - 1][groupColumnIndex]) {
                if (current.length > 0) result.push(current);
                current = [];
            }
            current.push(row);
        });
        if (current.length > 0) result.push(current);
        return result;
    }, [rows]);

    // 🎯 BEZPEČNÉ SLOUČENÍ SLOUPCŮ (rowSpan)
    const mergeIndices = useMemo(() => {
        const indices = [0]; // RNK (index 0) slučujeme v rámci týmu vždy

        if (!isTeam) {
            // Madison: Slučujeme Madison BIB a body
            indices.push(groupColumnIndex); 
            const pointsIdx = headers.findIndex((h) => h && h.toLowerCase().includes('points'));
            if (pointsIdx !== -1) indices.push(pointsIdx);
        } else {
            // Team Sprint: Pojistíme sloupce explicitně podle klíčových slov v záhlaví
            headers.forEach((header, idx) => {
                if (header) {
                    const hLower = header.toLowerCase();
                    if (hLower.includes('time') || hLower.includes('category')) {
                        indices.push(idx);
                    }
                }
            });
        }
        return indices;
    }, [isTeam, headers]);

    // Filtrace záhlaví pro desktop table: U Team Sprintu schováme sloupec B (Team BIB)
    const displayHeaders = useMemo(() => {
        if (isTeam) {
            return headers.filter((_, i) => i !== groupColumnIndex);
        }
        return headers;
    }, [headers, isTeam]);

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';

    // 2. BEZPEČNOSTNÍ POJISTKA (Guard Clause) POSUNUTÁ POD HOOKY 🛡️
    if (!values || values.length === 0 || !values[0]) return null;

    // =========================================================
    // 📱 MOBILNÍ ZOBRAZENÍ
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2, mb: 2 }}>
                {title && <Typography variant="h6" fontWeight={700} sx={{ px: 1 }}>{title}</Typography>}
                {teams.map((team, index) => {
                    const firstRider = team[0];
                    if (!firstRider) return null;

                    const rank = team.find(r => r[0] && r[0].trim() !== '')?.[0] || '';
                    const bib = firstRider[groupColumnIndex];
                    
                    const pointsIdx = headers.findIndex((h) => h && h.toLowerCase().includes('points'));
                    const timeIdx = headers.findIndex((h) => h && h.toLowerCase().includes('time'));
                    const catIdx = headers.findIndex((h) => h && h.toLowerCase().includes('category'));

                    let finalValue = '';
                    if (!isTeam && pointsIdx !== -1) {
                        finalValue = `${firstRider[pointsIdx]} pts`;
                    } else if (timeIdx !== -1) {
                        finalValue = firstRider[timeIdx];
                    } else if (catIdx !== -1) {
                        finalValue = firstRider[catIdx];
                    }

                    return (
                        <Paper key={index} sx={{ p: 2, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 45 }}>
                                    {rank && (
                                        <Typography fontWeight={700} sx={{ fontSize: '0.9rem', textAlign: 'center' }}>
                                            {isAbandoned(rank) ? '' : (rank.match(/^\d+$/) ? `#${rank}` : rank)}
                                        </Typography>
                                    )}
                                    {!isTeam && bib && <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>{bib}</Typography>}
                                </Box>

                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {team.map((rider, rIdx) => {
                                        const country = countryColIndex !== -1 ? rider[countryColIndex] : '';
                                        return (
                                            <Box key={rIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>{rider[2] ?? ''}</Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pl: 0.5 }}>{rider[3] ?? ''}</Typography>
                                                </Box>
                                                {country && (
                                                    <img src={getFlagUrl(country)} alt={country} style={{ width: '22px', height: '15px', objectFit: 'cover', borderRadius: '2px' }} />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {finalValue && (
                                    <Box sx={{ minWidth: 65, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                        <Typography fontWeight={700} variant="body2">{finalValue}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    // =========================================================
    // 🖥️ DESKTOP ZOBRAZENÍ
    // =========================================================
    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3, mt: 2, mb: 2, overflowX: 'auto' }}>
            {title && (
                <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.900', color: 'common.white', textAlign: 'center' }}>
                    <Typography fontWeight={600} variant="subtitle1">{title}</Typography>
                </Box>
            )}
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {displayHeaders.map((header, index) => (
                            <TableCell
                                key={index}
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'grey.900',
                                    color: 'common.white',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {teams.map((team, teamIndex) =>
                        team.map((row, rowIndex) => {
                            const isLastInTeam = rowIndex === team.length - 1;
                            const isLastTeamInTable = teamIndex === teams.length - 1;

                            const currentBorder = isLastInTeam && !isLastTeamInTable
                                ? '3px solid rgba(0, 0, 0, 0.87)' 
                                : '1px solid rgba(224, 224, 224, 1)';

                            return (
                                <TableRow key={`${teamIndex}-${rowIndex}`}>
                                    {headers.map((_, cellIndex) => {
                                        if (isTeam && cellIndex === groupColumnIndex) return null;

                                        const cellValue = row[cellIndex];
                                        const abandoned = isAbandoned(cellValue);
                                        const qualified = cellIndex === qColIndex && cellValue === 'Q';
                                        const isMerged = mergeIndices.includes(cellIndex);

                                        if (isMerged && rowIndex > 0) return null;

                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                rowSpan={isMerged ? team.length : 1}
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: cellIndex === 0 || abandoned ? 700 : 400,
                                                    borderBottom: isMerged 
                                                        ? (!isLastTeamInTable ? '3px solid rgba(0, 0, 0, 0.87)' : '1px solid rgba(224, 224, 224, 1)')
                                                        : currentBorder
                                                }}
                                            >
                                                {qualified ? (
                                                    <span className="qualified-badge">Q</span>
                                                ) : cellIndex === countryColIndex && cellValue ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <img src={getFlagUrl(cellValue)} alt={cellValue} style={{ width: '24px' }} />
                                                    </Box>
                                                ) : (
                                                    cellValue ?? '—'
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};